import React, { useEffect, useRef, useState } from 'react';
import css from 'styled-jsx/css';
import classnames from 'classnames';
import Link from 'next/link';

import { colors, dimensions, fonts } from '../common/Theme';
import { HREF as PROFILE_HREF } from '../../../pages/profile';
import { HREF as TRAVELERS_HREF } from '../../../pages/profile/travelers';
import { HREF as PAYMENTS_HREF } from '../../../pages/profile/payment-methods';

const componentStyles = css`
  .nav {
    position: relative;
  }

  .nav-list {
    display: flex;
    margin: 0 0 40px;
    padding: 0;
    border-bottom: 1px solid ${colors.ywgVeryLightBlue};
    overflow-x: auto;
    scrollbar-width: none;
  }

  .nav-list::before,
  .nav-list::after {
    opacity: 0;
    content: '';
    width: 20px;
    position: absolute;
    height: 100%;
    transition: opacity 0.3s ease;
    z-index: -1;
  }

  .nav-list::before {
    left: 0;
    background: linear-gradient(to left, transparent, white 85%, white);
  }

  .nav-list::after {
    right: 0;
    background: linear-gradient(to right, transparent, white 85%, white);
  }

  .nav-list--more-left::before,
  .nav-list--more-right::after {
    opacity: 1;
    z-index: 0;
  }

  .nav-list::-webkit-scrollbar {
    width: 0px;
    background: transparent;
  }

  .nav-list-item {
    display: flex;
    margin-bottom: 5px;
    margin-right: 30px;
    list-style: none;
  }

  .nav-list-item:last-of-type {
    margin-right: 0;
  }

  .nav-list-link {
    padding: 4px 0;
    color: ${colors.ywgNavy};
    font-size: 0.875rem;
    font-family: ${fonts.primaryFont};
    text-decoration: none;
    white-space: nowrap;
    outline: none;
  }

  .nav-list-link:hover,
  .nav-list-link:focus,
  .nav-list-link--current {
    color: ${colors.ywgPrimary};
    font-weight: 700;
    box-shadow: 0 5px 0 0 currentColor;
  }

  .nav-list-link:active {
    color: ${colors.ywgPinkishOrangeStrong};
  }

  @media (min-width: ${dimensions.ywgProfileMaxWidth}) {
    .nav-list-item {
      margin-right: 48px;
    }

    .nav-list-link {
      padding: 12px 0;
      font-size: 1.25rem;
    }
  }
`;

const observer = state => {
  const thresholdLimit = 0.9;
  return new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (entry.intersectionRatio > thresholdLimit) {
          state(false);
        } else {
          state(true);
        }
      });
    },
    {
      threshold: [0, thresholdLimit]
    }
  );
};

const Nav = ({ current }) => {
  const [navMoreLeft, setNavMoreLeft] = useState(false);
  const [navMoreRight, setNavMoreRight] = useState(false);
  const navList = useRef(null);
  const first = useRef(null);
  const last = useRef(null);

  const links = [
    {
      href: PROFILE_HREF,
      text: 'Dashboard'
    },
    {
      href: TRAVELERS_HREF,
      text: 'Profils voyageurs'
    },
    {
      href: PAYMENTS_HREF,
      text: 'Moyens de paiement'
    }
  ];

  useEffect(() => {
    if ('IntersectionObserver' in window) {
      const observerLeft = observer(setNavMoreLeft);
      observerLeft.observe(first.current);
      const observerRight = observer(setNavMoreRight);
      observerRight.observe(last.current);
      return () => {
        observerLeft.disconnect();
        observerRight.disconnect();
      };
    }
  }, [navMoreLeft, navMoreRight]);

  return (
    <>
      <nav className="nav">
        <ul
          className={classnames('nav-list', {
            'nav-list--more-left': navMoreLeft,
            'nav-list--more-right': navMoreRight
          })}
          ref={navList}
        >
          {links.map((link, i) => {
            const isCurrent = link.href === current;
            const isFirst = i === 0 ? first : null;
            const isLast = i + 1 === links.length ? last : null;
            const ref = isFirst || isLast || null;
            return (
              <li
                key={`profile-nav-${link.href}`}
                className={classnames('nav-list-item', {
                  'nav-list-item--current': isCurrent
                })}
                ref={ref}
              >
                <Link href={link.href}>
                  <a
                    className={classnames('nav-list-link', {
                      'nav-list-link--current': isCurrent
                    })}
                  >
                    {link.text}
                  </a>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
      <style jsx>{componentStyles}</style>
    </>
  );
};

export default Nav;
